import {
  scanCompletedBack,
  scanCompletedFront,
  setCardIdBack,
  setCardIdFront,
  setOcrDataFront,
} from '~/actions/addInsurance';
import { setRuntimeVariable } from '~/actions/runtime';
import { getInsurerIdByInsurerCode } from '~/core/util/insurers';
import { buildDateStringFromUserInput, deconstructDateString } from '~/core/util/date';
import { getInsurerCodeFromName } from './insurers';
import { capitalCase, capitalize, isString } from '~/core/util/string';
import { doValuesMatch, isEmptyObject } from '~/core/util/object';
import { OriginType } from '~/core/util/bookingFlowRoutingTypes';
import {
  ADD_INSURANCE_ORIGIN_APP_BOOKING,
  ADD_INSURANCE_ORIGIN_BOOKING_WIDGET,
  ADD_INSURANCE_ORIGIN_USER_PROFILE,
} from '~/core/util/bookingFlowRouting';
import localizedStr, { EN_US, ES_US, SupportedLocale } from '../../../core/util/localizedStr';
import { neverDefault } from '~/core/util/neverDefault';

const makeFields = (locale: SupportedLocale) => ({
  dobYear: { id: 'dobYear', label: capitalCase(localizedStr[locale].YEAR, { locale }) },
  dobMonth: {
    id: 'dobMonth',
    label: capitalCase(localizedStr[locale].BIRTH_DATE_MONTH, { locale }),
  },
  dobDay: { id: 'dobDay', label: capitalCase(localizedStr[locale].DAY, { locale }) },
  insuranceCarrier: {
    id: 'insuranceCarrier',
    label: capitalCase(localizedStr[locale].INSURANCE_CARRIER, { locale }),
  },
  insuranceType: {
    id: 'insuranceType',
    label: capitalCase(localizedStr[locale].INSURANCE_TYPE, { locale }),
  },
  memberId: { id: 'memberId', label: localizedStr[locale].MEMBER_ID },
  firstName: { id: 'firstName', label: capitalCase(localizedStr[locale].FIRST_NAME, { locale }) },
  lastName: { id: 'lastName', label: capitalCase(localizedStr[locale].LAST_NAME, { locale }) },
});

const fieldsByLocale: { [locale in SupportedLocale]: ReturnType<typeof makeFields> } = {
  [EN_US]: makeFields(EN_US),
  [ES_US]: makeFields(ES_US),
};

export const getFields = (locale?: SupportedLocale) => {
  if (locale == null) {
    return fieldsByLocale[EN_US];
  }

  if (locale === EN_US || locale === ES_US) {
    return fieldsByLocale[locale];
  }

  return neverDefault(locale, fieldsByLocale[EN_US]);
};

/** @deprecated */
export const fields = fieldsByLocale[EN_US];

const isMatchingProfiles = (profile1: any, profile2: any) =>
  doValuesMatch(
    [profile1, profile2],
    ['firstName', 'lastName'],
    (val1, val2) =>
      isString(val1) && isString(val2) && val1.trim().toLowerCase() === val2.trim().toLowerCase()
  );

const getInitialValues = (state: any) => {
  /* If there is no data from addInsurance state, use the newBooking data
   * If there is addInsurance data, check if profiles match and return a combination
   * Otherwise, return the addInsurance data
   */
  const { day, month, year } = deconstructDateString(state.newBooking.profile.birthDate);
  if (isEmptyObject(state.addInsurance.insurance)) {
    return {
      dobDay: day,
      dobMonth: month,
      dobYear: year,
      ...state.newBooking.profile,
    };
  }

  const { dobDay, dobMonth, dobYear, ...insuranceProfile } = state.addInsurance.insurance;
  if (isMatchingProfiles(state.addInsurance.insurance, state.newBooking.profile)) {
    return {
      dobDay: dobDay || day,
      dobMonth: dobMonth || month,
      dobYear: dobYear || year,
      ...state.newBooking.profile,
      ...insuranceProfile,
    };
  }

  return state.addInsurance.insurance;
};

const buildInsuranceProfilePostData = (insuranceForm = {}, accountId: any, insurers = []) => {
  const insuranceCarrierName =
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'insuranceCarrier' does not exist on type... Remove this comment to see the full error message
    isString(insuranceForm.insuranceCarrier) && insuranceForm.insuranceCarrier.trim();
  return {
    account_id: accountId,
    insurer_id: getInsurerIdByInsurerCode(
      getInsurerCodeFromName(insurers, insuranceCarrierName),
      insurers
    ),
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'insuranceType' does not exist on type '{... Remove this comment to see the full error message
    insurer_type: insuranceForm.insuranceType,
    insurer_name: insuranceCarrierName,
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'memberId' does not exist on type '{}'.
    member_code: insuranceForm.memberId,
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'firstName' does not exist on type '{}'.
    first_name: insuranceForm.firstName,
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'lastName' does not exist on type '{}'.
    last_name: insuranceForm.lastName,
    birth_date: buildDateStringFromUserInput(
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'dobYear' does not exist on type '{}'.
      insuranceForm.dobYear,
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'dobMonth' does not exist on type '{}'.
      insuranceForm.dobMonth,
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'dobDay' does not exist on type '{}'.
      insuranceForm.dobDay
    ),
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'cardFront' does not exist on type '{}'.
    card_front_id: insuranceForm.cardFront,
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'cardBack' does not exist on type '{}'.
    card_back_id: insuranceForm.cardBack,
  };
};

const buildInsuranceCoveragePostData = (
  insuranceForm = {},
  location = {},
  insurers = [],
  userProfileId = null,
  insuranceProfileId = null
) => {
  const insuranceCarrierName =
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'insuranceCarrier' does not exist on type... Remove this comment to see the full error message
    isString(insuranceForm.insuranceCarrier) && insuranceForm.insuranceCarrier.trim();
  return {
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'memberId' does not exist on type '{}'.
    member_code: insuranceForm.memberId,
    insurer_name: insuranceCarrierName,
    insurer_code: getInsurerCodeFromName(insurers, insuranceCarrierName),
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'insuranceType' does not exist on type '{... Remove this comment to see the full error message
    plan_type: insuranceForm.insuranceType,
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'firstName' does not exist on type '{}'.
    first_name: insuranceForm.firstName,
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'lastName' does not exist on type '{}'.
    last_name: insuranceForm.lastName,
    birth_date: buildDateStringFromUserInput(
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'dobYear' does not exist on type '{}'.
      insuranceForm.dobYear,
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'dobMonth' does not exist on type '{}'.
      insuranceForm.dobMonth,
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'dobDay' does not exist on type '{}'.
      insuranceForm.dobDay
    ),
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'npi' does not exist on type '{}'.
    npi_list_str: location.npi,
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'id' does not exist on type '{}'.
    location_id: location.id,
    user_profile_id: userProfileId,
    insurance_profile_id: insuranceProfileId,
  };
};

const buildNewBookingInsuranceData = (
  insuranceForm = {},
  insurers = [],
  userProfileId = null,
  accountId = null
) => {
  const insuranceCarrierName =
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'insuranceCarrier' does not exist on type... Remove this comment to see the full error message
    isString(insuranceForm.insuranceCarrier) && insuranceForm.insuranceCarrier.trim();
  return {
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'memberId' does not exist on type '{}'.
    memberCode: insuranceForm.memberId,
    insurerName: insuranceCarrierName,
    insurerCode: getInsurerCodeFromName(insurers, insuranceCarrierName),
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'insuranceType' does not exist on type '{... Remove this comment to see the full error message
    insurerType: insuranceForm.insuranceType,
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'firstName' does not exist on type '{}'.
    firstName: insuranceForm.firstName,
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'lastName' does not exist on type '{}'.
    lastName: insuranceForm.lastName,
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'cardFront' does not exist on type '{}'.
    cardFront: insuranceForm.cardFront,
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'cardBack' does not exist on type '{}'.
    cardBack: insuranceForm.cardBack,
    birthDate: buildDateStringFromUserInput(
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'dobYear' does not exist on type '{}'.
      insuranceForm.dobYear,
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'dobMonth' does not exist on type '{}'.
      insuranceForm.dobMonth,
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'dobDay' does not exist on type '{}'.
      insuranceForm.dobDay
    ),
    userProfileId,
    accountId,
  };
};

const dispatchInsuranceForEdit = (dispatch: any) => (insuranceProfile: any) => {
  const { day, month, year } = deconstructDateString(insuranceProfile.birth_date);
  dispatch(
    setOcrDataFront({
      firstName: insuranceProfile.first_name,
      lastName: insuranceProfile.last_name,
      insuranceCarrier: insuranceProfile.insurer_name,
      insuranceType: insuranceProfile.insurer_type,
      memberId: insuranceProfile.member_code,
      dobDay: day,
      dobMonth: month,
      dobYear: year,
    })
  );
  dispatch(setCardIdFront(insuranceProfile.card_front_id));
  dispatch(setCardIdBack(insuranceProfile.card_back_id));
  dispatch(scanCompletedFront());
  dispatch(scanCompletedBack());
  dispatch(setRuntimeVariable({ name: 'isEditFlow', value: true }));
};

const getSkipButtonCopy = (
  origin: OriginType,
  isInsuranceRequiredForBooking: boolean,
  isMobileFlow: boolean,
  isInsuranceFormPage: boolean,
  { locale = EN_US }: { locale?: SupportedLocale } = {}
): string | null => {
  const handleMobileFlow = () => {
    const skipAddingInsuranceMessage = capitalize(localizedStr[locale].SKIP_ADDING_INSURANCE, {
      locale,
    });
    if (isMobileFlow) {
      if (isInsuranceFormPage) {
        return skipAddingInsuranceMessage;
      }
      return capitalize(localizedStr[locale].SKIP_UPLOADING_PHOTOS, { locale });
    }
    return skipAddingInsuranceMessage;
  };

  // CDP flow
  if (origin === ADD_INSURANCE_ORIGIN_APP_BOOKING) {
    if (isInsuranceRequiredForBooking) {
      return capitalize(localizedStr[locale].IM_NOT_USING_INSURANCE, { locale });
    }
    return handleMobileFlow();
  }
  // Booking widget flow
  if (origin === ADD_INSURANCE_ORIGIN_BOOKING_WIDGET) {
    if (isInsuranceRequiredForBooking) {
      return null;
    }
    return handleMobileFlow();
  }
  // Account AddInsurance flow
  if (origin === ADD_INSURANCE_ORIGIN_USER_PROFILE) {
    if (isInsuranceFormPage) {
      return handleMobileFlow();
    }
    return capitalize(localizedStr[locale].SKIP_UPLOADING_PHOTOS, { locale });
  }

  return null;
};

export {
  buildInsuranceCoveragePostData,
  buildInsuranceProfilePostData,
  buildNewBookingInsuranceData,
  dispatchInsuranceForEdit,
  isMatchingProfiles,
  getInitialValues,
  getSkipButtonCopy,
};
